import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Text,
  Group,
  Box,
  Stack,
  Avatar,
  Button,
  Textarea,
  TextInput,
  ActionIcon,
  Select,
  Tooltip,
  Flex,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  Calendar as CalendarIcon,
  Clock,
  User,
  Monitor,
  ArrowLeft,
  X,
} from 'react-feather';
import { participantPortalApi } from '../../../services/api/participantPortal.js';
import type {
  ParticipantGroupCoachingCoach,
  ParticipantHuddleType,
  CalendlyAvailableSlot,
} from '../../../services/api/participantPortal.js';
import { CHECKIN_DURATION_MINUTES } from '../../../types';
import { useAuthStore } from '../../../stores/auth.js';
import { AvailabilityFailureAlert } from '../../../components/scheduling/AvailabilityFailureAlert.js';
import { SlotPicker } from '../../../components/scheduling/SlotPicker.js';
import { useMonthAvailability } from '../../../components/scheduling/useMonthAvailability.js';
import { InviteSentModal } from './InviteSentModal.js';
import { rescheduleConfirmText } from './rescheduleConfirmText.js';
import { getTimezoneShortName } from '../../../components/scheduling/slotPickerUtils.js';

// ============================================================================
// Types
// ============================================================================

// MED-1365: 'invite-sent' follows each booking and reschedule.
type ModalStep = 'select-date' | 'confirm' | 'invite-sent';

// ============================================================================
// Helpers
// ============================================================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function formatConfirmDate(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });
}

function formatConfirmTime(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

// ============================================================================
// Props
// ============================================================================

interface ScheduleCheckinModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  coach: ParticipantGroupCoachingCoach;
  cohortId: string;
  cohortName: string;
  /** When true, this is a reschedule (cancel + rebook). Requires activeCheckinId. */
  rescheduleMode?: boolean;
  /** The existing check-in session ID (required for reschedule mode). */
  activeCheckinId?: string;
  /** MED-1365 QA round 4: how the existing check-in was booked. */
  activeCheckinSchedulingMethod?: string | null;
  /** MED-699: Pre-selected check-in type (Intro / Midpoint / General). Defaults to 'general'. */
  initialHuddleType?: ParticipantHuddleType;
  /** MED-699: Types to disable in the dropdown (already scheduled or completed). */
  disabledHuddleTypes?: ParticipantHuddleType[];
  /** MED-699: Reason text for each disabled type (e.g., "Already scheduled"). */
  disabledHuddleTypeReasons?: Partial<Record<ParticipantHuddleType, string>>;
}

const HUDDLE_TYPE_OPTIONS: Array<{ value: ParticipantHuddleType; label: string }> = [
  { value: 'intro', label: 'Intro check-in' },
  { value: 'midpoint_checkin', label: 'Midpoint check-in' },
  { value: 'general', label: 'General check-in' },
];

const HUDDLE_TITLES: Record<ParticipantHuddleType, string> = {
  intro: 'Intro check-in',
  midpoint_checkin: 'Midpoint check-in',
  general: '1:1 Coach Check-In',
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleCheckinModal({
  opened,
  onClose,
  onSuccess,
  coach,
  cohortId,
  cohortName,
  rescheduleMode = false,
  activeCheckinId,
  activeCheckinSchedulingMethod,
  initialHuddleType = 'general',
  disabledHuddleTypes = [],
  disabledHuddleTypeReasons = {},
}: ScheduleCheckinModalProps) {
  const { user } = useAuthStore();
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  // MED-1319: phones and small tablets get a full-screen modal with the
  // picker stacked under the coach info.
  const stacked = useMediaQuery('(max-width: 62em)', false, { getInitialValueInEffect: false });

  // MED-699: selected check-in type (Intro / Midpoint / General)
  const [huddleType, setHuddleType] = useState<ParticipantHuddleType>(initialHuddleType);
  useEffect(() => {
    if (opened) setHuddleType(initialHuddleType);
  }, [opened, initialHuddleType]);

  // Selection state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CalendlyAvailableSlot | null>(null);
  const [step, setStep] = useState<ModalStep>('select-date');
  // MED-1365: true when Medley sent a Google invite (calendly_fallback).
  const [sentFallback, setSentFallback] = useState(false);

  // Confirm form
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MED-1319: every check-in books the coach's 20 min Calendly event type.
  const durationMinutes = CHECKIN_DURATION_MINUTES;
  // MED-1365 QA round 4: a reschedule names its session, so the times keep
  // the check-in's own slot.
  const movingId = rescheduleMode ? activeCheckinId : undefined;

  // Availability for the visible month, fetched in 7-day windows.
  const availability = useMonthAvailability<CalendlyAvailableSlot>({
    enabled: opened && coach.calendly_connected && coach.checkin_event_configured,
    sourceKey: `${coach.id}:${movingId ?? ''}`,
    timezone,
    fetchWindow: (start, end) =>
      participantPortalApi.getCheckinCoachAvailability(coach.id, start, end, movingId),
  });
  const { reset: resetAvailability } = availability;

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setSelectedDate(null);
      setSelectedSlot(null);
      setStep('select-date');
      setNotes('');
      setError(null);
      resetAvailability();
    }
  }, [opened, resetAvailability]);

  const handleDayClick = (dateKey: string) => {
    setSelectedDate(dateKey);
    setSelectedSlot(null);
  };

  const handleSlotClick = (slot: CalendlyAvailableSlot) => setSelectedSlot(slot);
  const handleNext = () => { if (selectedSlot) { setStep('confirm'); setError(null); } };
  const handleBack = () => { setStep('select-date'); setError(null); };

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const result =
        rescheduleMode && activeCheckinId
          ? await participantPortalApi.rescheduleSession(activeCheckinId, {
              scheduled_start: selectedSlot.start_time,
              timezone,
              notes: notes.trim() || undefined,
            })
          : await participantPortalApi.scheduleCheckin({
              cohort_id: cohortId,
              scheduled_start: selectedSlot.start_time,
              timezone,
              notes: notes.trim() || undefined,
              huddle_type: huddleType,
            });
      // MED-1365: tell the participant where the invite comes from. The
      // dashboard refreshes when they close the confirmation.
      setSentFallback(!!result.calendly_fallback);
      setStep('invite-sent');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : rescheduleMode
            ? 'Failed to reschedule check-in. Please try again.'
            : 'Failed to schedule check-in. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // Render: Invite sent (MED-1365)
  // ============================================================================

  if (step === 'invite-sent' && selectedSlot) {
    return (
      <InviteSentModal
        opened={opened}
        onDone={() => {
          onSuccess();
          onClose();
        }}
        heading={rescheduleMode ? 'Check-in rescheduled' : 'Check-in booked'}
        sessionTitle={HUDDLE_TITLES[huddleType]}
        dateText={formatConfirmDate(selectedSlot.start_time, timezone)}
        startTime={selectedSlot.start_time}
        timeText={formatConfirmTime(selectedSlot.start_time, timezone)}
        durationMinutes={durationMinutes}
        timezone={timezone}
        coachName={`${coach.first_name} ${coach.last_name}`}
        email={user?.email ?? ''}
        fallback={sentFallback}
      />
    );
  }

  // ============================================================================
  // Render: Confirm Step
  // ============================================================================

  if (step === 'confirm' && selectedSlot) {
    const coachName = `${coach.first_name} ${coach.last_name}`;

    return (
      <Modal opened={opened} onClose={onClose} size="md" radius="lg" withCloseButton={false} padding={0}>
        <Group justify="space-between" px="lg" py="md" style={{ borderBottom: '1px solid #E8E6E1' }}>
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={handleBack} size="sm">
              <ArrowLeft size={16} />
            </ActionIcon>
            <Text fw={700} size="lg">{rescheduleMode ? 'Confirm reschedule' : 'Confirm your check-in'}</Text>
          </Group>
          <ActionIcon variant="subtle" color="gray" onClick={onClose} size="sm" aria-label="Close">
            <X size={16} />
          </ActionIcon>
        </Group>

        <Stack gap="md" px="lg" py="md">
          <Box p="md" style={{ backgroundColor: '#F5F3EF', borderRadius: 8, border: '1px solid #E8E6E1' }}>
            <Text fw={600} size="sm" mb="xs">{HUDDLE_TITLES[huddleType]}</Text>
            <Stack gap={6}>
              <Group gap={8} wrap="nowrap">
                <CalendarIcon size={14} color="#888" />
                <Text size="sm" c="dimmed">{formatConfirmDate(selectedSlot.start_time, timezone)}</Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Clock size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  <Text span fw={600} c="dark">{formatConfirmTime(selectedSlot.start_time, timezone)}</Text>
                  {' '}&middot; {durationMinutes} min &middot;{' '}
                  {getTimezoneShortName(
                    timezone,
                    new Date(selectedSlot.start_time)
                  )}
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <User size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  {coachName}{coach.job_title ? ` \u00B7 ${coach.job_title}` : ''}
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Monitor size={14} color="#888" />
                <Text size="sm" c="dimmed">Meeting link will be included</Text>
              </Group>
            </Stack>
          </Box>

          <TextInput
            label="Your Name"
            value={user ? `${user.first_name} ${user.last_name}` : ''}
            readOnly
            styles={{ input: { backgroundColor: '#F5F3EF', borderColor: '#E8E6E1', color: '#1A1A1A' } }}
          />

          <TextInput
            label="Your Email"
            value={user?.email ?? ''}
            readOnly
            styles={{ input: { backgroundColor: '#F5F3EF', borderColor: '#E8E6E1', color: '#1A1A1A' } }}
          />

          <Textarea
            label={
              <Group gap={4}>
                <Text size="sm" fw={500}>Notes for your coach</Text>
                <Text size="sm" c="dimmed">(optional)</Text>
              </Group>
            }
            value={notes}
            onChange={e => setNotes(e.currentTarget.value)}
            placeholder="Anything you'd like to discuss..."
            minRows={3}
            autosize
            styles={{ input: { borderColor: '#E8E6E1' } }}
          />

          {error && <Text size="sm" c="red">{error}</Text>}

          <Button
            fullWidth
            size="md"
            loading={isSubmitting}
            onClick={handleSubmit}
            style={{ backgroundColor: '#2D4739', border: 'none' }}
          >
            {rescheduleMode ? 'Reschedule & Send Invite' : 'Schedule & Send Invite'}
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            {rescheduleMode
              ? rescheduleConfirmText('check-in', activeCheckinSchedulingMethod, coachName)
              : `A calendar invite will be sent to you and ${coachName}.`}
          </Text>
        </Stack>
      </Modal>
    );
  }

  // ============================================================================
  // Render: Select Date & Time
  // ============================================================================

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={932}
      fullScreen={stacked}
      radius={stacked ? 0 : 'lg'}
      withCloseButton={false}
      padding={0}
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        onClick={onClose}
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
        size={stacked ? 44 : 'sm'}
        aria-label="Close"
      >
        <X size={16} />
      </ActionIcon>

      <Flex direction={stacked ? 'column' : 'row'} wrap="nowrap" align="stretch" style={{ minHeight: stacked ? undefined : 520 }}>
        {/* LEFT PANEL: Coach Info */}
        <Box
          p="lg"
          style={{
            width: stacked ? 'auto' : 240, minWidth: stacked ? 0 : 240,
            borderRight: stacked ? 'none' : '1px solid #E8E6E1',
            borderBottom: stacked ? '1px solid #E8E6E1' : 'none',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          }}
        >
          <Box>
            <Avatar size={48} radius="xl" color="medleyGreen" variant="filled" mb="sm">
              {getInitials(coach.first_name, coach.last_name)}
            </Avatar>
            <Text size="xs" c="dimmed" mb={2}>
              {coach.first_name} {coach.last_name}
            </Text>
            <Text fw={700} size="lg" mb={rescheduleMode ? 'md' : 'xs'}>
              {HUDDLE_TITLES[huddleType]}
            </Text>

            {/* MED-699 AC7/AC17: Type dropdown - disabled options carry tooltip reason */}
            {!rescheduleMode && (
              <Tooltip
                label={disabledHuddleTypeReasons[huddleType] ?? ''}
                disabled={!disabledHuddleTypes.includes(huddleType)}
                withArrow
              >
                <Select
                  size="xs"
                  mb="md"
                  value={huddleType}
                  onChange={v => {
                    if (!v) return;
                    const next = v as ParticipantHuddleType;
                    if (disabledHuddleTypes.includes(next)) return;
                    setHuddleType(next);
                  }}
                  data={HUDDLE_TYPE_OPTIONS.map(opt => ({
                    value: opt.value,
                    label: disabledHuddleTypes.includes(opt.value)
                      ? `${opt.label} - ${disabledHuddleTypeReasons[opt.value] ?? 'Unavailable'}`
                      : opt.label,
                    disabled: disabledHuddleTypes.includes(opt.value),
                  }))}
                  aria-label="Check-in type"
                />
              </Tooltip>
            )}

            <Stack gap={8}>
              <Group gap={8} wrap="nowrap">
                <Clock size={14} color="#888" />
                <Text size="sm" c="dimmed">{durationMinutes} min</Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Monitor size={14} color="#888" />
                <Text size="sm" c="dimmed">Video meeting</Text>
              </Group>
            </Stack>
          </Box>

          <Box mt="xl" p="xs" style={{ backgroundColor: '#F5F3EF', borderRadius: 8, borderLeft: '3px solid #E8E6E1' }}>
            <Text size="xs" c="dimmed">Group Coaching &middot; {cohortName}</Text>
          </Box>
        </Box>

        {/* On phones the picker needs its own padding; on desktop its two
          panels stay direct children of the row. */}
        <Box p={stacked ? 'lg' : undefined} style={{ display: stacked ? 'block' : 'contents' }}>
          <SlotPicker
            layout={stacked ? 'stacked' : 'panels'}
            availability={availability}
            timezone={timezone}
            selectedDate={selectedDate}
            onSelectDate={handleDayClick}
            onClearSelection={() => {
              setSelectedDate(null);
              setSelectedSlot(null);
            }}
            selectedSlot={selectedSlot}
            onSelectSlot={handleSlotClick}
            onNext={handleNext}
            topStatus={
              !availability.isLoading && availability.failure != null && (
                <AvailabilityFailureAlert onRetry={availability.reload} />
              )
            }
          />
        </Box>
      </Flex>
    </Modal>
  );
}
